let domReadyPromise: Promise<void> | null = null;

/**
 * Resolves when the initial document has been parsed and the DOM is ready to use.
 * All callers share the same promise within the current module instance.
 *
 * @example
 * async function initializePage(): Promise<void> {
 *     await whenDomReady();
 *     document.querySelector("main")?.classList.add("initialized");
 * }
 */
export function whenDomReady(): Promise<void> {
    domReadyPromise ??=
        document.readyState === "loading"
            ? new Promise<void>((resolve) => {
                  document.addEventListener("DOMContentLoaded", () => resolve(), { once: true });
              })
            : Promise.resolve();

    return domReadyPromise;
}

/**
 * Executes a callback when the initial document DOM is ready to use.
 *
 * @param callback Function to execute when the DOM is ready.
 * @example
 * documentReady(() => initializePage());
 * @deprecated Use {@link whenDomReady} and await the returned promise instead.
 */
export function documentReady(callback: () => void): void {
    if (document.readyState !== "loading") {
        return callback();
    } else {
        document.addEventListener("DOMContentLoaded", callback);
    }
}
